import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CommunicationChannel, CommunicationChannelType } from 'ngx-modbus';

import { BluetoothLeService } from './bluetooth-le.service';
import { InternetService } from './internet.service';
import { LanService } from './lan.service';
import { InternetZCSService } from './internet.zcs.service';
import { InternetEthZCSService } from './internet.eth.zcs.service';
import { InternetChannelFactory } from './internet-channel.factory';
import { InternetChannelInstance } from './internet-channel.instance';
import { InternetAHService } from './internet.ah.service';

const CH_DEBUG = { ENABLED: true };
const chLog = (msg: string) => CH_DEBUG.ENABLED && console.log(`[Modbus:Handler] ${msg}`);

export type ChannelType = {
  type: CommunicationChannelType.Lan;
  channel: LanService;
} | {
  type: CommunicationChannelType.BluetoothLe;
  channel: BluetoothLeService;
} | {
  type: CommunicationChannelType.Internet;
  channel: InternetService | InternetChannelInstance;
} | {
  type: CommunicationChannelType.InternetMQTT_ZCS;
  channel: InternetZCSService;
} | {
  type: CommunicationChannelType.InternetETHLongPolling_ZCS;
  channel: InternetEthZCSService;
} | {
  type: CommunicationChannelType.InternetAH_ZCS;
  channel: InternetAHService;
};

export type MultiChannelEntry = {
  type: CommunicationChannelType;
  channel: CommunicationChannel;
};

@Injectable({
  providedIn: 'root',
})
export class ChannelHandler {
  isDesktop = Capacitor.getPlatform() === 'web';

  /** Primary single-device handler (retrocompat) */
  handler: ChannelType = {
    type: CommunicationChannelType.Internet,
    channel: this.internetService,
  };

  /**
   * Multi-device channel registry keyed by deviceId.
   * Only Internet channels are stored here (one per device via factory).
   * BLE/LAN use singleton services and are not tracked in this Map.
   */
  private readonly channels = new Map<string, MultiChannelEntry>();

  /** Currently active device ID (the one synced to handler via connectChannel) */
  private _activeDeviceId: string | null = null;

  get activeDeviceId(): string | null {
    return this._activeDeviceId;
  }

  constructor(
    private readonly bluetoothLeService: BluetoothLeService,
    private readonly internetService: InternetService,
    private readonly internetZCSService: InternetZCSService,
    private readonly internetEthZCSService: InternetEthZCSService,
    private readonly internetAHService: InternetAHService,
    private readonly lanService: LanService,
    private readonly internetChannelFactory: InternetChannelFactory,
  ) { }

  /** Switch primary handler (single-device retrocompat) */
  switch(type: CommunicationChannelType) {
    if (type === CommunicationChannelType.BluetoothLe) {
      this.handler = {
        type,
        channel: this.bluetoothLeService,
      };
    } else if (type === CommunicationChannelType.Lan) {
      this.handler = {
        type,
        channel: this.lanService,
      };
    } else if (type === CommunicationChannelType.Internet) {
      this.handler = {
        type,
        channel: this.internetService,
      };
    } else if (type === CommunicationChannelType.InternetMQTT_ZCS) {
      this.handler = {
        type,
        channel: this.internetZCSService,
      };
    } else if (type === CommunicationChannelType.InternetETHLongPolling_ZCS) {
      this.handler = {
        type,
        channel: this.internetEthZCSService,
      };
    } else if (type === CommunicationChannelType.InternetAH_ZCS) {
      this.handler = {
        type,
        channel: this.internetAHService,
      };
    }
  }

  // ─── Multi-device API ─────────────────────────────────────────────

  /**
   * Get or create an isolated channel for a specific device.
   * For Internet channels, creates a new InternetChannelInstance via factory.
   * Caller must still call `channel.open(...)` after this.
   */
  getOrCreateChannel(deviceId: string, type: CommunicationChannelType): CommunicationChannel {
    const existing = this.channels.get(deviceId);
    if (existing) {
      if (!existing.channel.isConnected) {
        chLog(` getOrCreateChannel("${deviceId}") - evicting dead channel (isConnected=false)`);
        this.channels.delete(deviceId);
      } else {
        chLog(` getOrCreateChannel("${deviceId}") - reusing existing (type=${existing.type})`);
        return existing.channel;
      }
    }

    let channel: CommunicationChannel;

    if (type === CommunicationChannelType.Internet) {
      channel = this.internetChannelFactory.create();
    } else {
      // BLE and LAN are physical connections (one device at a time).
      // ZCS channels (MQTT/ETH) can be added in a follow-up with their own factories.
      chLog(`Multi-device not yet supported for type="${type}", falling back to singleton`);
      this.switch(type);
      return this.handler.channel;
    }

    this.channels.set(deviceId, { type, channel });
    chLog(` getOrCreateChannel("${deviceId}") - created new channel (type=${type}, total=${this.channels.size})`);
    return channel;
  }

  /**
   * Prepare a channel for a device via the registry. Cleans up the previous active
   * channel if switching to a different device. For Internet type, creates an isolated
   * channel via the factory. For other types, falls back to singleton switch().
   * Syncs the result to `handler` for retrocompat.
   *
   * **Important:** The returned channel is NOT yet connected — the caller must
   * invoke `channel.open(...)` / `channel.openByInverterSerial(...)` after this.
   */
  async connectChannel(deviceId: string, type: CommunicationChannelType): Promise<CommunicationChannel> {
    chLog(` connectChannel("${deviceId}", type=${type}) — activeDeviceId="${this._activeDeviceId}"`);

    // Clean up previous active channel if switching to a different device
    if (this._activeDeviceId && this._activeDeviceId !== deviceId) {
      const prev = this.channels.get(this._activeDeviceId);
      if (prev) {
        if (prev.channel.isConnected) {
          chLog(` connectChannel — closing previous active channel "${this._activeDeviceId}"`);
          await prev.channel.close();
        }
        this.channels.delete(this._activeDeviceId);
        chLog(` connectChannel — removed previous channel from registry (remaining=${this.channels.size})`);
      }
      this._activeDeviceId = null;
    }

    if (type === CommunicationChannelType.Internet) {
      const channel = this.getOrCreateChannel(deviceId, type);
      // Sync to handler for retrocompat (device pages read from handler)
      this.handler = { type, channel: channel as InternetService | InternetChannelInstance };
      this._activeDeviceId = deviceId;
      chLog(` connectChannel — handler synced to registry channel, activeDeviceId="${deviceId}"`);
      return channel;
    }

    // Non-Internet: fall back to singleton switch (BLE, LAN, ZCS)
    this.switch(type);
    this._activeDeviceId = null;
    chLog(` connectChannel — using singleton for type=${type}`);
    return this.handler.channel;
  }

  /** Get an active channel by device ID. Reserved for future multi-device navigation. */
  getChannel(deviceId: string): MultiChannelEntry | undefined {
    return this.channels.get(deviceId);
  }

  /** All active multi-device channels. Reserved for future multi-device navigation. */
  get activeChannels(): ReadonlyMap<string, MultiChannelEntry> {
    return this.channels;
  }

  /** Close and remove a specific device channel */
  async closeChannel(deviceId: string): Promise<void> {
    const entry = this.channels.get(deviceId);
    if (entry) {
      chLog(` closeChannel("${deviceId}") - type=${entry.type}`);
      await entry.channel.close();
      this.channels.delete(deviceId);
      if (this._activeDeviceId === deviceId) {
        this._activeDeviceId = null;
        chLog(` closeChannel — cleared activeDeviceId`);
      }
      chLog(` Channel closed. Remaining: ${this.channels.size}`);
    } else {
      chLog(` closeChannel("${deviceId}") - no channel found`);
    }
  }

  /** Close all multi-device channels */
  async closeAllChannels(): Promise<void> {
    chLog(` closeAllChannels() - closing ${this.channels.size} channel(s)`);
    for (const [id, entry] of this.channels) {
      chLog(` Closing "${id}" (type=${entry.type})`);
      await entry.channel.close();
    }
    this.channels.clear();
    this._activeDeviceId = null;
    chLog(` All channels closed`);
  }
}
